import React, { useState } from 'react';
import { collection, getDocs, doc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { toast } from 'react-toastify';
import logger from '../../utils/logger';

/**
 * Customer Recovery Panel
 *
 * This component helps identify recently deleted customers by analyzing:
 * 1. Jobs without matching customer records
 * 2. Invoices without matching customer records
 * 3. Vehicles without matching customer records
 *
 * It provides a UI to restore orphaned customer data
 */
const RecoveryPanel = () => {
  const [orphanedRecords, setOrphanedRecords] = useState([]);
  const [isScanning, setIsScanning] = useState(false);

  // Scan for orphaned records that reference deleted customers
  const scanForOrphanedRecords = async () => {
    setIsScanning(true);
    const orphaned = [];

    try {
      // Get all current customers
      const customersSnapshot = await getDocs(collection(db, 'customers'));
      const customerIds = new Set(customersSnapshot.docs.map(doc => doc.id));

      // Scan jobs for missing customer references
      logger.info('Scanning jobs...');
      const jobsSnapshot = await getDocs(collection(db, 'jobs'));
      const jobCustomerData = new Map();

      jobsSnapshot.forEach(jobDoc => {
        const job = jobDoc.data();
        const customerId = job.customerId;

        if (customerId && !customerIds.has(customerId)) {
          // Found a job referencing a deleted customer
          if (!jobCustomerData.has(customerId)) {
            jobCustomerData.set(customerId, {
              customerId,
              customerName: job.customerName,
              customerEmail: job.customerEmail,
              customerPhone: job.customerPhone,
              customerAddress: job.customerAddress,
              relatedJobs: [],
              relatedVehicles: [],
              relatedInvoices: []
            });
          }

          jobCustomerData.get(customerId).relatedJobs.push({
            jobCardNo: job.jobCardNo,
            date: job.createdAt,
            vehicleReg: job.registrationNumber
          });
        }
      });

      // Scan vehicles for missing customer references
      logger.info('Scanning vehicles...');
      const vehiclesSnapshot = await getDocs(collection(db, 'vehicles'));

      vehiclesSnapshot.forEach(vehicleDoc => {
        const vehicle = vehicleDoc.data();
        const customerId = vehicle.customerId;

        if (customerId && !customerIds.has(customerId)) {
          if (!jobCustomerData.has(customerId)) {
            jobCustomerData.set(customerId, {
              customerId,
              customerName: vehicle.customerName || 'Unknown',
              relatedJobs: [],
              relatedVehicles: [],
              relatedInvoices: []
            });
          }

          jobCustomerData.get(customerId).relatedVehicles.push({
            vehicleId: vehicleDoc.id,
            make: vehicle.vehicleMake,
            model: vehicle.vehicleModel,
            registration: vehicle.registrationNumber
          });
        }
      });

      // Scan invoices for missing customer references
      logger.info('Scanning invoices...');
      const invoicesSnapshot = await getDocs(collection(db, 'newInvoices'));

      invoicesSnapshot.forEach(invoiceDoc => {
        const invoice = invoiceDoc.data();
        const customerName = invoice.customerName;

        // Try to find matching orphaned record by name
        // eslint-disable-next-line no-unused-vars
        for (const [customerId, data] of jobCustomerData.entries()) {
          if (data.customerName === customerName) {
            data.relatedInvoices.push({
              invoiceNumber: invoice.invoiceNumber,
              date: invoice.invoiceDate,
              amount: invoice.receivable
            });
          }
        }
      });

      // Convert Map to Array
      orphaned.push(...Array.from(jobCustomerData.values()));

      setOrphanedRecords(orphaned);

      if (orphaned.length === 0) {
        toast.info('No orphaned records found. All customer references are valid.');
      } else {
        toast.success(`Found ${orphaned.length} orphaned customer record(s) that may need recovery!`);
      }

    } catch (error) {
      logger.error('Error scanning for orphaned records:', error);
      toast.error('Failed to scan for orphaned records');
    } finally {
      setIsScanning(false);
    }
  };

  // Restore a customer record
  const handleRestore = async (record) => {
    try {
      const customerData = {
        firstName: record.customerName?.split(' ')[0] || '',
        lastName: record.customerName?.split(' ').slice(1).join(' ') || '',
        email: record.customerEmail || '',
        phoneNumber: record.customerPhone || '',
        address: record.customerAddress || '',
        createdAt: Timestamp.now(),
        restoredAt: Timestamp.now(),
        restoredFromOrphaned: true
      };

      // Use the original customer ID if available
      if (record.customerId) {
        await setDoc(doc(db, 'customers', record.customerId), customerData);
        toast.success(`Successfully restored customer: ${record.customerName}`);
      } else {
        toast.error('Customer ID not available. Cannot restore without ID.');
      }

      // Refresh the scan
      scanForOrphanedRecords();

    } catch (error) {
      logger.error('Error restoring customer:', error);
      toast.error('Failed to restore customer record');
    }
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-lg">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Customer Recovery Panel</h2>
          <p className="text-sm text-gray-600 mt-1">
            Scan for and recover accidentally deleted customer records
          </p>
        </div>
        <button
          onClick={scanForOrphanedRecords}
          disabled={isScanning}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          {isScanning ? (
            <>
              <span className="animate-spin">⏳</span>
              <span>Scanning...</span>
            </>
          ) : (
            <>
              <span>🔍</span>
              <span>Scan for Deleted Customers</span>
            </>
          )}
        </button>
      </div>

      {/* Instructions */}
      <div className="mb-6 p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
        <h3 className="font-semibold text-blue-900 mb-2">How This Works:</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Scans Jobs, Vehicles, and Invoices for references to deleted customers</li>
          <li>• Identifies orphaned records with customer data</li>
          <li>• Allows you to restore customer records with their original data</li>
          <li>• Works best within 24 hours of deletion</li>
        </ul>
      </div>

      {/* Results */}
      {orphanedRecords.length > 0 && (
        <div className="space-y-4">
          <div className="p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded">
            <p className="text-yellow-800 font-semibold">
              ⚠️ Found {orphanedRecords.length} Deleted Customer Record(s)
            </p>
          </div>

          {orphanedRecords.map((record, index) => (
            <div key={index} className="border rounded-lg p-4 bg-gray-50">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">
                    {record.customerName || 'Unknown Customer'}
                  </h3>
                  <p className="text-sm text-gray-600">ID: {record.customerId}</p>
                  {record.customerEmail && (
                    <p className="text-sm text-gray-600">Email: {record.customerEmail}</p>
                  )}
                  {record.customerPhone && (
                    <p className="text-sm text-gray-600">Phone: {record.customerPhone}</p>
                  )}
                </div>
                <button
                  onClick={() => handleRestore(record)}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm font-semibold"
                >
                  ↺ Restore Customer
                </button>
              </div>

              {/* Related Records */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                {/* Jobs */}
                {record.relatedJobs.length > 0 && (
                  <div className="bg-white p-3 rounded border">
                    <h4 className="font-semibold text-sm text-gray-700 mb-2">
                      Related Jobs ({record.relatedJobs.length})
                    </h4>
                    <ul className="text-xs space-y-1">
                      {record.relatedJobs.slice(0, 3).map((job, idx) => (
                        <li key={idx} className="text-gray-600">
                          • {job.jobCardNo} - {job.vehicleReg}
                        </li>
                      ))}
                      {record.relatedJobs.length > 3 && (
                        <li className="text-gray-500 italic">
                          + {record.relatedJobs.length - 3} more
                        </li>
                      )}
                    </ul>
                  </div>
                )}

                {/* Vehicles */}
                {record.relatedVehicles.length > 0 && (
                  <div className="bg-white p-3 rounded border">
                    <h4 className="font-semibold text-sm text-gray-700 mb-2">
                      Related Vehicles ({record.relatedVehicles.length})
                    </h4>
                    <ul className="text-xs space-y-1">
                      {record.relatedVehicles.slice(0, 3).map((vehicle, idx) => (
                        <li key={idx} className="text-gray-600">
                          • {vehicle.make} {vehicle.model} - {vehicle.registration}
                        </li>
                      ))}
                      {record.relatedVehicles.length > 3 && (
                        <li className="text-gray-500 italic">
                          + {record.relatedVehicles.length - 3} more
                        </li>
                      )}
                    </ul>
                  </div>
                )}

                {/* Invoices */}
                {record.relatedInvoices.length > 0 && (
                  <div className="bg-white p-3 rounded border">
                    <h4 className="font-semibold text-sm text-gray-700 mb-2">
                      Related Invoices ({record.relatedInvoices.length})
                    </h4>
                    <ul className="text-xs space-y-1">
                      {record.relatedInvoices.slice(0, 3).map((invoice, idx) => (
                        <li key={idx} className="text-gray-600">
                          • {invoice.invoiceNumber} - ₦{invoice.amount?.toLocaleString()}
                        </li>
                      ))}
                      {record.relatedInvoices.length > 3 && (
                        <li className="text-gray-500 italic">
                          + {record.relatedInvoices.length - 3} more
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!isScanning && orphanedRecords.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <div className="text-6xl mb-4">🔍</div>
          <p>Click "Scan for Deleted Customers" to start the recovery process</p>
        </div>
      )}
    </div>
  );
};

export default RecoveryPanel;
